import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { modelProviderApi, type ModelProviderConfig, type LlmProvider, type ModelUseCase } from '@/lib/api';
import {
  Cpu, Plus, Trash2, Check, X, Edit2, ToggleLeft, ToggleRight, RefreshCw,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import styles from './_auth.settings.llm-providers.module.css';

export const Route = createFileRoute('/_auth/settings/llm-providers')({
  component: LlmProvidersPage,
});

const PROVIDER_LABELS: Record<LlmProvider, { name: string; color: string; logo: string }> = {
  GOOGLE_GEMINI: { name: 'Google Gemini', color: '#4285f4', logo: '🧠' },
  OPENAI:        { name: 'OpenAI',        color: '#10a37f', logo: '⚡' },
  ANTHROPIC:     { name: 'Anthropic',     color: '#d97706', logo: '🔶' },
};

const USE_CASE_LABELS: Record<ModelUseCase, string> = {
  CONVERSATION:   'Conversation',
  ROUTING:        'Routing',
  SUMMARIZATION:  'Summarization',
  EMBEDDING:      'Embedding',
};

const PROVIDERS: LlmProvider[] = ['GOOGLE_GEMINI', 'OPENAI', 'ANTHROPIC'];
const USE_CASES: ModelUseCase[] = ['CONVERSATION', 'ROUTING', 'SUMMARIZATION', 'EMBEDDING'];

interface FormState {
  provider: LlmProvider;
  modelName: string;
  useCase: ModelUseCase;
  apiKey: string;
  priority: number;
}

const DEFAULT_MODELS: Record<LlmProvider, string> = {
  GOOGLE_GEMINI: 'gemini-1.5-flash',
  OPENAI: 'gpt-4o-mini',
  ANTHROPIC: 'claude-3-5-haiku-20241022',
};

export default function LlmProvidersPage() {
  const [configs, setConfigs] = useState<ModelProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<ModelProviderConfig | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ModelProviderConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({
    provider: 'GOOGLE_GEMINI',
    modelName: DEFAULT_MODELS.GOOGLE_GEMINI,
    useCase: 'CONVERSATION',
    apiKey: '',
    priority: 10,
  });

  const load = () => {
    setLoading(true);
    modelProviderApi.list().then(setConfigs).finally(() => setLoading(false));
  };
  useEffect(load, []);

  function openCreate() {
    setEditTarget(null);
    setForm({ provider: 'GOOGLE_GEMINI', modelName: DEFAULT_MODELS.GOOGLE_GEMINI, useCase: 'CONVERSATION', apiKey: '', priority: 10 });
    setShowModal(true);
  }

  function openEdit(config: ModelProviderConfig) {
    setEditTarget(config);
    setForm({
      provider: config.provider,
      modelName: config.modelName,
      useCase: config.useCase,
      apiKey: '', // never pre-fill key
      priority: config.priority,
    });
    setShowModal(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await modelProviderApi.upsert({
        id: editTarget?.id,
        provider: form.provider,
        modelName: form.modelName,
        useCase: form.useCase,
        priority: form.priority,
        enabled: true,
        apiKey: form.apiKey || undefined,
      });
      setShowModal(false);
      load();
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(config: ModelProviderConfig) {
    await modelProviderApi.upsert({
      id: config.id,
      provider: config.provider,
      modelName: config.modelName,
      useCase: config.useCase,
      enabled: !config.enabled,
    });
    load();
  }

  async function handleDelete(config: ModelProviderConfig) {
    await modelProviderApi.delete(config.id);
    setDeleteTarget(null);
    load();
  }

  const tenantConfigs = configs.filter(c => c.tenantId !== null);
  const platformConfigs = configs.filter(c => c.tenantId === null);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><Cpu size={22} /> LLM Providers</h1>
          <p className={styles.subtitle}>
            Configure per-tenant API keys. Platform defaults are used when no tenant key is set.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button id="refresh-providers" className={styles.refreshBtn} onClick={load}>
            <RefreshCw size={14} />
          </button>
          <button id="add-provider" className={styles.addBtn} onClick={openCreate}>
            <Plus size={16} /> Add Provider
          </button>
        </div>
      </div>

      {/* Tenant-level configs */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Your Configuration</h2>
        <p className={styles.sectionHint}>API keys set here override platform defaults for this tenant.</p>

        {loading && <div className={styles.skeleton} />}

        {!loading && tenantConfigs.length === 0 && (
          <div className={styles.empty}>
            <Cpu size={32} className={styles.emptyIcon} />
            <p>No tenant-specific providers. Using platform defaults below.</p>
            <button className={styles.addBtn} onClick={openCreate}><Plus size={14} /> Add your own key</button>
          </div>
        )}

        {!loading && tenantConfigs.length > 0 && (
          <div className={styles.configGrid}>
            {tenantConfigs.map(cfg => (
              <ConfigCard
                key={cfg.id}
                config={cfg}
                onEdit={() => openEdit(cfg)}
                onToggle={() => handleToggle(cfg)}
                onDelete={() => setDeleteTarget(cfg)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Platform defaults */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Platform Defaults</h2>
        <p className={styles.sectionHint}>
          Shared across all tenants. These use the platform's API keys and cannot be modified here.
        </p>
        {!loading && platformConfigs.length > 0 && (
          <div className={styles.configGrid}>
            {platformConfigs.map(cfg => (
              <ConfigCard key={cfg.id} config={cfg} readonly />
            ))}
          </div>
        )}
      </section>

      {/* Modal */}
      {showModal && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{editTarget ? 'Edit Provider' : 'Add LLM Provider'}</h2>
              <button className={styles.closeBtn} onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.field}>
                <label className={styles.label}>Provider</label>
                <div className={styles.providerGrid}>
                  {PROVIDERS.map(p => (
                    <button
                      key={p}
                      id={`provider-${p}`}
                      type="button"
                      className={`${styles.providerBtn} ${form.provider === p ? styles.selected : ''}`}
                      onClick={() => setForm(f => ({ ...f, provider: p, modelName: DEFAULT_MODELS[p] }))}
                    >
                      <span className={styles.providerLogo}>{PROVIDER_LABELS[p].logo}</span>
                      <span>{PROVIDER_LABELS[p].name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.row2}>
                <div className={styles.field}>
                  <label className={styles.label}>Model Name</label>
                  <input
                    id="model-name"
                    className={styles.input}
                    value={form.modelName}
                    onChange={e => setForm(f => ({ ...f, modelName: e.target.value }))}
                    placeholder={DEFAULT_MODELS[form.provider]}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Use Case</label>
                  <select
                    id="use-case"
                    className={styles.select}
                    value={form.useCase}
                    onChange={e => setForm(f => ({ ...f, useCase: e.target.value as ModelUseCase }))}
                  >
                    {USE_CASES.map(u => <option key={u} value={u}>{USE_CASE_LABELS[u]}</option>)}
                  </select>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  API Key {editTarget && <span className={styles.optional}>(leave blank to keep existing)</span>}
                </label>
                <input
                  id="api-key"
                  type="password"
                  className={styles.input}
                  value={form.apiKey}
                  onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
                  placeholder={editTarget ? '••••••••••••••••' : `Your ${PROVIDER_LABELS[form.provider].name} API key`}
                  autoComplete="new-password"
                />
                <p className={styles.hint}>Stored encrypted. Never logged or exposed after saving.</p>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Priority</label>
                <p className={styles.hint}>Lower = tried first. Tenant configs always beat platform defaults.</p>
                <input
                  id="provider-priority"
                  type="number"
                  className={styles.input}
                  style={{ maxWidth: 100 }}
                  value={form.priority}
                  onChange={e => setForm(f => ({ ...f, priority: Number(e.target.value) }))}
                  min={1}
                  max={999}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button
                id="save-provider"
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving || !form.modelName.trim() || (!editTarget && !form.apiKey.trim())}
              >
                {saving ? 'Saving…' : <><Check size={14} /> {editTarget ? 'Update' : 'Add Provider'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title={`Remove ${deleteTarget?.provider ? PROVIDER_LABELS[deleteTarget.provider]?.name : ''} provider?`}
        description={`This will remove ${deleteTarget?.modelName ?? 'this model'} configuration. If no fallback provider exists, conversations may break.`}
        confirmLabel="Remove Provider"
        variant="danger"
      />
    </div>
  );
}

function ConfigCard({
  config,
  readonly = false,
  onEdit,
  onToggle,
  onDelete,
}: {
  config: ModelProviderConfig;
  readonly?: boolean;
  onEdit?: () => void;
  onToggle?: () => void;
  onDelete?: () => void;
}) {
  const provInfo = PROVIDER_LABELS[config.provider];

  return (
    <div className={`${styles.configCard} ${!config.enabled ? styles.disabled : ''}`}>
      <div className={styles.cardTop}>
        <div className={styles.providerBadge} style={{ borderColor: provInfo.color + '44', background: provInfo.color + '11' }}>
          <span>{provInfo.logo}</span>
          <span style={{ color: provInfo.color }}>{provInfo.name}</span>
        </div>
        {!readonly && (
          <div className={styles.cardActions}>
            <button title="Edit" className={styles.iconBtn} onClick={onEdit}><Edit2 size={13} /></button>
            <button title={config.enabled ? 'Disable' : 'Enable'} className={styles.iconBtn} onClick={onToggle}>
              {config.enabled ? <ToggleRight size={16} style={{ color: 'var(--color-success)' }} /> : <ToggleLeft size={16} />}
            </button>
            <button title="Remove" className={`${styles.iconBtn} ${styles.danger}`} onClick={onDelete}><Trash2 size={13} /></button>
          </div>
        )}
        {readonly && <span className={styles.platformBadge}>Platform</span>}
      </div>

      <div className={styles.cardModel}>{config.modelName}</div>

      <div className={styles.cardMeta}>
        <span className={styles.metaChip}>{USE_CASE_LABELS[config.useCase]}</span>
        <span className={styles.metaChip}>Priority {config.priority}</span>
        <span className={styles.metaChip}>{config.encryptedKey ? '🔑 Key set' : '🔓 No key'}</span>
      </div>

      {!config.enabled && <div className={styles.disabledBadge}>Disabled</div>}
    </div>
  );
}
